import {
  ConfigProvider,
  StaticHostConfig,
  SubdomainProxyProvider,
} from '@aboutyou/config';
import {BotModeProvider, GrpcConfig} from '@aboutyou/data-fetcher';
import {FeatureFlagProvider, FeatureFlagSetup} from '@aboutyou/feature-flags';
import {HeadContext, HeadElementsProvider} from '@aboutyou/head-elements';
import {PageStateProvider} from '@aboutyou/history-page-state';
import {I18nProvider, StaticI18nConfig} from '@aboutyou/i18n';
import {
  CanGoBackProvider,
  RouterProvider,
  RouterProviderProps,
  useCompleteLocation,
  useIsLastInteractionUIPushEvent,
} from '@aboutyou/router';
import {StorageProvider, StorageState} from '@aboutyou/storage';
import React, {useRef} from 'react';
import {GrpcClientHostConfigProvider} from './providers/GrpcClientHostConfigProvider';
import {TrackingProvider} from './providers/TrackingProvider';

interface FeaturesProviderProps {
  headContext: HeadContext;
  i18nConfig: StaticI18nConfig;
  hostConfig: StaticHostConfig;
  routerConfig: RouterProviderProps;
  storage: StorageState;
  grpcConfig: GrpcConfig;
  featureFlagSetup: FeatureFlagSetup;
  documentReferrer: string;
  enableBotMode: boolean;
  isProxied: boolean;
  hasDroppedMobileSubdomain: boolean;
}

export const FeaturesProvider: React.FC<
  React.PropsWithChildren<FeaturesProviderProps>
> = ({
  children,
  headContext,
  i18nConfig,
  hostConfig,
  routerConfig,
  grpcConfig,
  featureFlagSetup,
  storage,
  documentReferrer,
  enableBotMode,
  isProxied,
  hasDroppedMobileSubdomain,
}) => {
  return (
    <BotModeProvider value={enableBotMode}>
      <RouterProvider {...routerConfig}>
        <StorageProvider {...storage}>
          <ConfigProvider config={hostConfig}>
            <SubdomainProxyProvider
              isProxied={isProxied}
              hasDroppedMobileSubdomain={hasDroppedMobileSubdomain}
            >
              <I18nProvider {...i18nConfig}>
                <FeatureFlagProvider {...featureFlagSetup}>
                  <TrackingProvider documentReferrer={documentReferrer}>
                    <GrpcClientHostConfigProvider config={grpcConfig}>
                      <HeadElementsProvider headContext={headContext}>
                        <PageStateProviderWithKey>
                          <CanGoBackProvider>{children}</CanGoBackProvider>
                        </PageStateProviderWithKey>
                      </HeadElementsProvider>
                    </GrpcClientHostConfigProvider>
                  </TrackingProvider>
                </FeatureFlagProvider>
              </I18nProvider>
            </SubdomainProxyProvider>
          </ConfigProvider>
        </StorageProvider>
      </RouterProvider>
    </BotModeProvider>
  );
};

export const PageStateProviderWithKey: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const location = useCompleteLocation();
  const isLastInteractionUIPushEvent = useIsLastInteractionUIPushEvent();
  const pageKeyRef = useRef(location.key);

  if (!isLastInteractionUIPushEvent) {
    pageKeyRef.current = location.key;
  }

  return (
    <PageStateProvider pageKey={pageKeyRef.current}>
      {children}
    </PageStateProvider>
  );
};
